import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Space, Spin, Typography, message } from 'antd';
import { CheckCircleOutlined, CopyOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';
import { getBaseInstanceId, getBaseUserId } from '../api/bitable';
import { renderHtmlOffscreen } from '../utils/renderOffscreen';
import { captureDocumentImage } from './capture';
import { signApi, SignQuotaError, type SignAreaPct } from './api';

/** 从离屏渲染的成品 body 实测各签名区域相对文档图的百分比坐标。 */
function measureSignAreas(body: HTMLElement): SignAreaPct[] {
  const host = (body.querySelector('.pp-ov-host') as HTMLElement) || body;
  const hostRect = host.getBoundingClientRect();
  const W = hostRect.width || 1;
  const H = hostRect.height || 1;
  const nodes = Array.from(body.querySelectorAll('.pp-ov[data-ov-kind="sign_area"]')) as HTMLElement[];
  return nodes.map((el) => {
    const r = el.getBoundingClientRect();
    return {
      id: el.dataset.ovId || '',
      label: el.dataset.ovLabel || '签署方',
      xPct: (r.left - hostRect.left) / W,
      yPct: (r.top - hostRect.top) / H,
      wPct: r.width / W,
      hPct: r.height / H,
    };
  });
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** 成品模式 HTML（无编辑描边/签名占位框），离屏渲染后截图。
   *  异步是因为附件临时链接过期时要先重取一次数据。 */
  buildOutputHtml: () => string | Promise<string>;
  docName: string;
  tableId: string;
  recordId: string | null;
  /** 纸张宽度（mm），离屏渲染按它设宽，否则截图比例和成品对不上。 */
  paperWidthMm?: number;
}

type Phase =
  | { step: 'idle' }
  | { step: 'working'; label: string }
  | { step: 'done'; shareUrl: string; qrDataUrl: string; remaining: number }
  | { step: 'quota'; remaining: number }
  | { step: 'error'; message: string };

export function SignModal({ open, onClose, buildOutputHtml, docName, tableId, recordId, paperWidthMm }: Props) {
  const [phase, setPhase] = useState<Phase>({ step: 'idle' });

  const run = useCallback(async () => {
    const outputHtml = await buildOutputHtml();
    if (!outputHtml || !recordId) {
      setPhase({ step: 'error', message: '预览尚未就绪或未选择记录' });
      return;
    }
    let cleanup: (() => void) | null = null;
    try {
      setPhase({ step: 'working', label: '正在生成文档图片…' });
      const rendered = await renderHtmlOffscreen(outputHtml, paperWidthMm);
      cleanup = rendered.cleanup;
      // 截图前先实测签名区域坐标（同一份 DOM，与图片像素自洽）
      const signAreas = measureSignAreas(rendered.body);
      const dataUrl = await captureDocumentImage(rendered.body);
      cleanup();
      cleanup = null;
      setPhase({ step: 'working', label: '正在上传文档…' });
      const imageUrl = await signApi.uploadImage(dataUrl);
      setPhase({ step: 'working', label: '正在创建签署链接…' });
      const [baseId, creatorId] = await Promise.all([getBaseInstanceId(), getBaseUserId()]);
      const r = await signApi.createSignLink({
        baseId,
        tableId,
        creatorId,
        docName: docName || '待签署文件',
        recordId,
        imageUrls: [imageUrl],
        signAreas,
      });
      const shareUrl = r.records[0]?.shareUrl || '';
      const qrDataUrl = await QRCode.toDataURL(shareUrl, { width: 220, margin: 1 });
      setPhase({ step: 'done', shareUrl, qrDataUrl, remaining: r.remainingQuota });
    } catch (err) {
      if (err instanceof SignQuotaError) {
        setPhase({ step: 'quota', remaining: err.remaining });
      } else {
        setPhase({ step: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    } finally {
      cleanup?.();
    }
  }, [buildOutputHtml, paperWidthMm, recordId, tableId, docName]);

  // 只在弹窗打开的那一刻跑一次。
  //
  // run 依赖 buildOutputHtml，而它的身份随预览数据变化；弹窗开着的时候，
  // 后台每 0.8 秒一次的勾选轮询、用户在表里点一下记录，都会让它换新身份，
  // 于是签署文档被反复上传、反复扣额度，链接还悄悄换成了另一条记录的。
  const runRef = useRef(run);
  runRef.current = run;
  const startedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setPhase({ step: 'idle' });
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    void runRef.current();
  }, [open]);

  const copy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => message.success('已复制'))
      .catch(() => message.warning('复制失败，请手动复制'));
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} title="发起签名" width={420} destroyOnClose>
      {phase.step === 'working' && (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <Spin />
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink-3)' }}>{phase.label}</div>
        </div>
      )}

      {phase.step === 'done' && (
        <Space direction="vertical" size={12} style={{ width: '100%', textAlign: 'center' }}>
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="签署链接已生成"
            description={`剩余签字次数：${phase.remaining}`}
          />
          <img src={phase.qrDataUrl} alt="签署二维码" style={{ width: 180, height: 180, margin: '0 auto' }} />
          <Typography.Text
            style={{ fontSize: 11.5, wordBreak: 'break-all', color: 'var(--ink-2)' }}
          >
            {phase.shareUrl}
          </Typography.Text>
          <Space>
            <Button type="primary" icon={<CopyOutlined />} onClick={() => copy(phase.shareUrl)}>
              复制链接
            </Button>
            <Button onClick={() => window.open(phase.shareUrl, '_blank', 'noopener,noreferrer')}>
              打开预览
            </Button>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
            把链接或二维码发给签署人，对方无需安装任何插件。
            签署进度与签好的文件在「签字确认」插件中查看管理。
          </Typography.Text>
        </Space>
      )}

      {phase.step === 'quota' && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="剩余签字次数不足"
            description={`当前剩余 ${phase.remaining} 次。签名能力由「签字确认」插件提供（每个账号免费 30 次），请在多维表格插件市场打开「签字确认」插件充值后再试。`}
          />
          <Button onClick={onClose} block>
            知道了
          </Button>
        </Space>
      )}

      {phase.step === 'error' && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert type="error" showIcon message="发起签名失败" description={phase.message} />
          <Button onClick={() => void run()} block>
            重试
          </Button>
        </Space>
      )}
    </Modal>
  );
}
